import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "previewContainer", "submitButton"]
  static values = {
    maxSize: { type: Number, default: 5 },
    maxFiles: { type: Number, default: 10 }
  }

  connect() {
    this.uploadCount = 0
    this.setupDragAndDrop()

    // Make existing images draggable
    const existingImages = this.previewContainerTarget.querySelectorAll('[data-existing-image]')
    existingImages.forEach(img => this.setupDraggablePreview(img))

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false // Allow initial form submission
    }
  }

  setupDragAndDrop() {
    this.previewContainerTarget.addEventListener('dragover', this.handleDragOver.bind(this))
    this.previewContainerTarget.addEventListener('drop', this.handleDrop.bind(this))
  }

  handleDragOver(e) {
    e.preventDefault()
    const draggingElement = this.previewContainerTarget.querySelector('[style*="opacity"]')
    if (!draggingElement) return

    const siblings = [...this.previewContainerTarget.querySelectorAll('.relative.group:not([style*="opacity"])')]
    const nextSibling = siblings.find(sibling => {
      const rect = sibling.getBoundingClientRect()
      return e.clientY <= rect.top + rect.height / 2
    })

    if (nextSibling) {
      this.previewContainerTarget.insertBefore(draggingElement, nextSibling)
    } else {
      this.previewContainerTarget.appendChild(draggingElement)
    }
  }

  handleDrop(e) {
    e.preventDefault()
    const draggingElement = this.previewContainerTarget.querySelector('[style*="opacity"]')
    if (draggingElement) {
      draggingElement.style.opacity = ''
      this.updateImageOrder()
    }
  }

  handleFiles(event) {
    const files = event.target.files
    if (!this.validateFiles(files)) {
      event.target.value = ''
      return
    }

    // Don't clear existing previews - we want to be able to add more photos
    Array.from(files).forEach(file => {
      if (file.type.startsWith('image/')) {
        this.createPreview(file)
      }
    })
  }

  validateFiles(files) {
    // Get count of existing images plus new files
    const existingCount = this.previewContainerTarget.querySelectorAll('[data-existing-image]').length
    const newPreviewCount = this.previewContainerTarget.querySelectorAll('[data-new-image]').length
    const totalCount = existingCount + newPreviewCount + files.length

    if (totalCount > this.maxFilesValue) {
      alert(`You can only have up to ${this.maxFilesValue} images total.`)
      return false
    }

    for (let file of files) {
      if (!file.type.startsWith('image/')) {
        alert('Please upload only image files.')
        return false
      }
      if (file.size > this.maxSizeValue * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is ${this.maxSizeValue}MB.`)
        return false
      }
    }
    return true
  }

  createPreview(file) {
    const reader = new FileReader()
    const previewContainer = document.createElement('div')
    previewContainer.className = 'relative group'
    previewContainer.setAttribute('data-new-image', '')

    reader.onload = (e) => {
      previewContainer.innerHTML = `
        <img src="${e.target.result}" 
             class="w-full h-48 object-cover rounded-lg" 
             alt="${file.name}"
             style="cursor: move">
        <div class="absolute top-2 right-2">
          <button type="button" 
                  class="remove-button bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                  data-action="click->image-preview#removePreview">
            ×
          </button>
        </div>
        <div class="absolute bottom-2 left-2 right-2">
          <div class="text-sm text-white bg-black bg-opacity-50 rounded p-1">
            ${file.name}
          </div>
        </div>
      `
      this.previewContainerTarget.appendChild(previewContainer)
      this.setupDraggablePreview(previewContainer)
    }

    reader.readAsDataURL(file)
  }

  setupDraggablePreview(previewElement) {
    previewElement.setAttribute('draggable', true)

    previewElement.addEventListener('dragstart', () => {
      previewElement.style.opacity = '0.5'
    })

    previewElement.addEventListener('dragend', () => {
      previewElement.style.opacity = ''
      this.updateImageOrder()
    })
  }

  updateImageOrder() {
    const previews = this.previewContainerTarget.querySelectorAll('[data-existing-image]')
    const orderedIds = Array.from(previews).map(preview => preview.dataset.signedId).filter(id => id)

    if (orderedIds.length > 0) {
      const designId = this.element.closest('form').getAttribute('action').split('/')[2]

      fetch(`/designs/${designId}/reorder_images`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({ image_ids: orderedIds })
      }).catch(error => {
        console.error('Error reordering images:', error)
        alert('Failed to save image order. Please try again.')
      })
    }
  }

  removePreview(event) {
    event.preventDefault()
    const previewElement = event.target.closest('[data-new-image]')?.parentElement
    if (!previewElement) return
    previewElement.remove()
  }

  startUpload() {
    this.uploadCount += 1
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
  }

  finishUpload() {
    this.uploadCount -= 1
    if (this.uploadCount === 0 && this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
    }
  }
}
